import type { QuoteCommercialTypeEnum, ResidenceTypeEnum } from '@/helpers/const';
import type { PeriodKey, PeriodicValue } from '@/types';
import type { FrequencyType, IDSType } from '@/types/api';

type IdsTypeMapping = {
  commercialType?: QuoteCommercialTypeEnum;
  idsType?: ResidenceTypeEnum;
};

export const frequency: Record<FrequencyType, PeriodKey> = {
  weekly: 'week',
  fortnightly: 'fortnight',
  monthly: 'month',
  quarterly: 'quarter',
  annually: 'year',
};

export const periodic = (amount?: number, freq?: FrequencyType): PeriodicValue => {
  return {
    amount: amount ?? 0,
    frequency: freq ? frequency[freq] : 'month',
  };
};

const IDS_TYPE_MAP: Record<IDSType, IdsTypeMapping> = {
  house: { idsType: 'house' },
  unit: { idsType: 'unit' },
  other: { idsType: 'other' },

  residential_investment_house: {
    commercialType: 'RESIDENTIAL_INVESTMENT',
    idsType: 'house',
  },
  residential_investment_unit: {
    commercialType: 'RESIDENTIAL_INVESTMENT',
    idsType: 'unit',
  },
  residential_investment_other: {
    commercialType: 'RESIDENTIAL_INVESTMENT',
    idsType: 'other',
  },

  residential_residual_stock_house: {
    commercialType: 'RESIDENTIAL_RESIDUAL_STOCK',
    idsType: 'house',
  },
  residential_residual_stock_unit: {
    commercialType: 'RESIDENTIAL_RESIDUAL_STOCK',
    idsType: 'unit',
  },
  residential_residual_stock_other: {
    commercialType: 'RESIDENTIAL_RESIDUAL_STOCK',
    idsType: 'other',
  },

  mixed_use: { commercialType: 'MIXED_USE' },
  office: { commercialType: 'OFFICE' },
  retail: { commercialType: 'RETAIL' },
  hospitality: { commercialType: 'HOSPITALITY' },
  industrial_warehouse: { commercialType: 'INDUSTRIAL_WAREHOUSE' },
  specialised: { commercialType: 'SPECIALISED' },
  vacant_land_land_banks: { commercialType: 'VACANT_LAND_LAND_BANKS' },
  commercial_residual_stock: { commercialType: 'COMMERCIAL_RESIDUAL_STOCK' },
  commercial: { commercialType: 'COMMERCIAL' },
  industrial: { commercialType: 'INDUSTRIAL' },
  vacant_land: { commercialType: 'VACANT_LAND' },
};
/**
 * Maps FE commercialType + idsType to the backend's single idsType field.
 * Residential commercial types are concatenated (e.g. RESIDENTIAL_INVESTMENT + house -> residential_investment_house).
 * Other commercial types map directly (e.g. OFFICE -> office).
 */
export const mapIdsTypeToApi = (
  commercialType?: QuoteCommercialTypeEnum,
  idsType?: ResidenceTypeEnum,
): IDSType | undefined => {
  if (!commercialType && !idsType) return undefined;

  const entry = Object.entries(IDS_TYPE_MAP).find(
    ([, value]) => value.commercialType === commercialType && value.idsType === idsType,
  );

  return entry?.[0] as IDSType | undefined;
};
/**
 * Reverse mapping: Converts backend idsType back to frontend commercialType + idsType
 * This is the inverse of mapIdsTypeToApi
 */
export const mapIdsTypeFromApi = (backendIdsType?: IDSType): IdsTypeMapping => {
  if (!backendIdsType) return {};

  return IDS_TYPE_MAP[backendIdsType] ?? {};
};
